<script setup lang="ts">
import { concat, debounce } from "lodash-es";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { bsonId } from "@/common/utils/common";
import { maskCPF, maskCNPJ } from "@/common/utils/mask";
import { toast } from "@/common/utils/toast";

import {
  analysisSignatureList,
  analysisSignatureCreate,
  analysisSignaturePatch,
  analysisSignatureDelete,
} from "@/common/api/rest/analysis/signature";

import {
  baseConfirm,
  baseInput,
  baseSelect,
  baseSignature,
  baseTextarea,
} from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: any;
    accountId: string;
    analysisId: string;
    width?: string;
    isEdit?: boolean;
  }>(),
  {
    width: "100%",
    isEdit: true,
  },
);

const DEFAULT_PAYLOAD = {
  type: "",
  type_name: null,
  name: "",
  document_type: "",
  document_name: "",
  document_number: "",
  signature: "",
  description: "",
};

const signatures = ref<any[]>([]);
const selected = ref<any | null>(null);

const showConfirmDel = ref(false);

const signatureOptions = computed(() => [
  {
    option: t("common.labels.administrator"),
    value: "ADMINISTRATOR",
  },
  {
    option: t("common.labels.assistant"),
    value: "ASSISTANT",
  },
  {
    option: t("common.labels.employee"),
    value: "EMPLOYEE",
  },
  {
    option: t("common.labels.manager"),
    value: "MANAGER",
  },
  {
    option: t("common.labels.technical"),
    value: "TECHNICAL",
  },
  {
    option: t("common.labels.other"),
    value: "OTHER",
  },
]);

const documentOptions = computed(() => [
  {
    option: t("common.labels.cpf"),
    value: "CPF",
  },
  {
    option: t("common.labels.cnpj"),
    value: "CNPJ",
  },
  {
    option: t("common.labels.other"),
    value: "OTHER",
  },
]);

function fecthSignatures() {
  analysisSignatureList(props.accountId, {
    analysis_id: props.analysisId,
    page: 0,
    limit: 100,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        signatures.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function addSignature() {
  const { name, social_name } = props.data?.technical ?? {};

  analysisSignatureCreate(props.accountId, {
    ...DEFAULT_PAYLOAD,
    evaluation_id: props.data.evaluation_id,
    analysis_id: props.data.analysis_id,
    type: "TECHNICAL",
    name: social_name || name,
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        toast({ message: message, type: "success" });
        emit("update-data");
        fecthSignatures();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function patch(index: number, payload: any) {
  const signature = signatures.value[index];

  analysisSignaturePatch(props.accountId, signature.signature_id, payload)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        toast({ message: message, type: "success" });
        emit("update-data");
        fecthSignatures();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function update(index: number, key: string) {
  const signature = signatures.value[index];
  analysisSignaturePatch(props.accountId, signature.signature_id, {
    [key]: signature[key],
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        toast({ message: message, type: "success" });
        emit("update-data");
        fecthSignatures();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function saveSignature(index: number, signature: string) {
  signatures.value[index].signature = signature;

  update(index, "signature");
}

function documentMask(index: number, value: any) {
  if (signatures.value[index].document_type === "CPF") return maskCPF(value);

  if (signatures.value[index].document_type === "CNPJ") return maskCNPJ(value);

  return value;
}

function changeType(index: number, type: string) {
  let payload: any = {
    type,
  };

  switch (type) {
    case "ASSISTANT":
      if (props.data?.assistant) {
        const { name, social_name } = props.data.assistant;

        payload = {
          ...DEFAULT_PAYLOAD,
          signature: payload.signature,
          name: social_name || name,
          document_type: "OTHER",
          document_name: null,
          document_number: null,
          type,
          type_name: null,
        };
      }
      break;

    case "TECHNICAL":
      if (props.data?.technical) {
        const { name, social_name } = props.data.technical;

        payload = {
          ...DEFAULT_PAYLOAD,
          signature: payload.signature,
          name: social_name || name,
          document_type: "OTHER",
          document_name: null,
          document_number: null,
          type,
          type_name: null,
        };
      }
      break;

    case "EMPLOYEE":
      if (props.data?.employee) {
        const {
          name,
          social_name,
          document_type,
          document_name,
          document_number,
        } = props.data.employee;

        payload = {
          ...DEFAULT_PAYLOAD,
          signature: payload.signature,
          name: social_name || name,
          document_type,
          document_name,
          document_number,
          type,
          type_name: null,
        };
      }

      break;

    default:
      payload = {
        ...DEFAULT_PAYLOAD,
        signature: payload.signature,
        document_type: "OTHER",
        document_name: null,
        document_number: null,
        type: payload.type,
        type_name: null,
      };
      break;
  }

  patch(index, payload);
}

function changeDocumentType(index: number, type: string | null) {
  signatures.value[index].document_name = type;
  signatures.value[index].document_number = documentMask(
    index,
    signatures.value[index].document_number,
  );

  if (type === "OTHER") {
    signatures.value[index].document_name = "";
  }

  patch(index, {
    document_name: signatures.value[index].document_name,
    document_number: signatures.value[index].document_number,
    document_type: signatures.value[index].document_type,
  });
}

function del(signature: any) {
  selected.value = signature;
  showConfirmDel.value = true;
}

function delCancel() {
  showConfirmDel.value = false;
  selected.value = null;
}

function delConfirm() {
  if (!selected.value) return;

  analysisSignatureDelete(props.accountId, selected.value.signature_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        delCancel();
        emit("update-data");
        fecthSignatures();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

onMounted(() => {
  fecthSignatures();
});
</script>

<template>
  <section class="tab-component" :class="{ width: width }">
    <div class="content">
      <template v-if="signatures.length === 0">
        <div class="box-empty">
          {{ t("analysis.texts.empty_signature") }}
          <button class="btn btn-md btn-primary" @click="addSignature()">
            {{
              t("common.labels.add", {
                concat: t("common.labels.signature"),
              }).toUpperCase()
            }}
          </button>
        </div>
      </template>

      <template v-else>
        <div class="signatures">
          <template v-for="(item, index) in signatures" :key="item">
            <div v-if="item" class="item">
              <div class="form">
                <base-select
                  v-model="item.type"
                  :value="item.type"
                  :options="signatureOptions"
                  :is-empty="false"
                  required
                  @change="changeType(index, $event)"
                >
                  <template #label> {{ t("common.labels.type") }} * </template>
                </base-select>

                <template v-if="item.type === 'OTHER'">
                  <base-input
                    v-model="item.type_name"
                    :value="item.type_name"
                    required
                    @blur="update(index, 'type_name')"
                  >
                    <template #label>
                      {{ t("common.labels.type_name") }} *</template
                    >
                  </base-input>
                </template>

                <base-input
                  v-model="item.name"
                  :value="item.name"
                  required
                  @blur="update(index, 'name')"
                >
                  <template #label> {{ t("common.labels.name") }} *</template>
                </base-input>

                <div class="documents">
                  <template v-if="item?.document_type === 'OTHER'">
                    <div class="flex flex-col gap-2">
                      <base-select
                        v-model="item.document_type"
                        :value="item.document_type"
                        :options="documentOptions"
                        required
                        @change="changeDocumentType(index, $event)"
                      >
                        <template #label>
                          {{ t("common.labels.document_type") }} *
                        </template>
                      </base-select>

                      <base-input
                        v-model="item.document_name"
                        :value="item.document_name"
                        :placeholder="t('common.placeholders.describe')"
                        required
                        @blur="update(index, 'document_name')"
                      >
                        <template #label>
                          {{ t("common.labels.document_name") }} *</template
                        >
                      </base-input>
                    </div>

                    <base-input
                      v-model="item.document_number"
                      :value="item.document_number"
                      :placeholder="t('common.labels.number')"
                      :mask="(event) => documentMask(index, event)"
                      required
                      @blur="update(index, 'document_number')"
                    >
                      <template #label>
                        {{ t("common.labels.number") }} *</template
                      >
                    </base-input>
                  </template>

                  <template v-else>
                    <div class="flex flex-col gap-2">
                      <base-select
                        v-model="item.document_type"
                        :value="item.document_type"
                        :options="documentOptions"
                        required
                        @change="changeDocumentType(index, $event)"
                      >
                        <template #label>
                          {{ t("common.labels.document_type") }} *
                        </template>
                      </base-select>

                      <base-input
                        v-model="item.document_number"
                        :value="item.document_number"
                        :placeholder="t('common.labels.number')"
                        :mask="(event) => documentMask(index, event)"
                        required
                        @blur="update(index, 'document_number')"
                      >
                        <template #label>
                          {{ t("common.labels.number") }} *</template
                        >
                      </base-input>
                    </div>
                  </template>
                </div>

                <base-textarea
                  v-model="item.description"
                  :value="item.description"
                  :placeholder="t('common.placeholders.describe')"
                  @blur="update(index, 'description')"
                >
                  <template #label>
                    {{ t("common.labels.description") }}
                  </template>
                </base-textarea>
              </div>

              <div class="sign">
                <base-signature
                  :signature="item.signature"
                  :show-actions="true"
                  :show-del="true"
                  width="100%"
                  @save="saveSignature(index, $event)"
                  @del="del(item)"
                />
              </div>
            </div>
          </template>
        </div>

        <div class="actions">
          <button class="btn btn-md btn-primary" @click="addSignature()">
            {{
              t("common.labels.add", {
                concat: t("common.labels.signature"),
              }).toUpperCase()
            }}
          </button>
        </div>
      </template>
    </div>
  </section>

  <base-confirm
    :show="showConfirmDel"
    max-width="400px"
    :title="
      t('common.labels.remove', {
        concat: t('common.labels.signature'),
      }).toUpperCase()
    "
    :message="
      t('common.texts.remove_message', { concat: t('common.labels.signature') })
    "
    @cancel="delCancel"
    @confirm="delConfirm"
  />
</template>

<style lang="scss" scoped>
.tab-component {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding-block: 1.5rem;
  padding-inline: 1.5rem;

  .title {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.15rem;
    font-weight: bold;
    text-transform: uppercase;
    border-bottom: 1px solid var(--border);
    padding-block-end: 0.25rem;
  }

  .signatures {
    display: flex;
    flex-direction: column;
    gap: 2rem;

    .item {
      display: flex;
      flex-direction: row;
      gap: 2rem;
      padding-block-end: 2rem;
      border-block-end: 1px solid var(--border);

      .sign {
        flex: 1;
      }

      .form {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }
    }
  }

  .documents {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .box-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.5rem;
    padding-block: 1.5rem;
  }

  .actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.5rem;
    padding-block-start: 3rem;
    padding-block-end: 1rem;
  }
}

@media screen and (max-width: 1024px) {
  .tab-component {
    .signatures {
      .item {
        flex-direction: column;
      }
    }
  }
}
</style>
